import { useEffect, useRef, type CompositionEventHandler, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { cn } from '@shared/lib/utils';
import { Text } from '../typography/text';
import { Button } from '../primitives/button';
import { Input } from '../primitives/input';
import { Label } from '../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../overlay/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../overlay/tooltip';
import { CountBadge } from '../feedback/count-badge';
import { PopoverSelect, type PopoverSelectOption } from './popover-select';

export interface FilterPopoverProps {
  activeFilterCount: number;
  children: ReactNode;
  clearLabel: string;
  disabled?: boolean;
  label: string;
  onClear?: () => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  testId?: string;
  triggerClassName?: string;
}

export interface FilterPopoverSelectProps {
  allLabel: string;
  disabled?: boolean;
  emptyText?: string;
  errorLabel?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Learning selectors intentionally suppress empty-state copy in the popover. */
  silentEmpty?: boolean;
  /** Learning retries a failed selector when its popover is reopened. */
  retryOnOpen?: boolean;
  endLabel?: string;
  hasNextPage?: boolean;
  loadedPageCount?: number;
  paginationScopeKey?: string;
  id: string;
  label: string;
  loading?: boolean;
  loadingLabel?: string;
  loadingMore?: boolean;
  loadingMoreLabel?: string;
  onChange: (value: string | undefined) => void;
  onListEndReached?: () => void;
  onSearchValueChange?: (value: string) => void;
  options: PopoverSelectOption[];
  searchPlaceholder?: string;
  searchValue?: string;
  showOptionsWithoutSearch?: boolean;
  value: string | undefined;
  compact?: boolean;
}

export interface FilterPopoverInputProps {
  disabled?: boolean;
  id: string;
  label: string;
  max?: string;
  min?: string;
  onChange: (value: string | undefined) => void;
  onCompositionEnd?: CompositionEventHandler<HTMLInputElement>;
  onCompositionStart?: CompositionEventHandler<HTMLInputElement>;
  placeholder?: string;
  preserveInputDraft?: boolean;
  step?: string;
  type?: 'date' | 'number' | 'search' | 'text';
  value: string | undefined;
}

export function FilterPopoverFields({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('space-y-4', className)}>{children}</div>;
}

export function FilterPopoverSelect({
  allLabel,
  disabled = false,
  emptyText,
  errorLabel,
  onRetry,
  retryLabel,
  silentEmpty = false,
  retryOnOpen = false,
  endLabel,
  hasNextPage,
  loadedPageCount,
  paginationScopeKey,
  id,
  label,
  loading = false,
  loadingLabel,
  loadingMore = false,
  loadingMoreLabel,
  onChange,
  onListEndReached,
  onSearchValueChange,
  options,
  searchPlaceholder,
  searchValue,
  showOptionsWithoutSearch,
  value,
  compact = false,
}: FilterPopoverSelectProps) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <PopoverSelect
        id={id}
        value={value ?? ''}
        options={[{ label: allLabel, value: '' }, ...options]}
        onValueChange={(nextValue) => {
          onChange(nextValue === '' ? undefined : nextValue);
        }}
        emptyText={silentEmpty ? '' : emptyText}
        aria-invalid={Boolean(errorLabel) && !loading}
        aria-describedby={errorLabel && !loading ? `${id}-error` : undefined}
        onOpenChange={(open) => {
          if (retryOnOpen && open && errorLabel && !loading && !loadingMore) onRetry?.();
        }}
        disabled={disabled}
        loading={loading}
        loadingLabel={loadingLabel ?? emptyText ?? 'Loading'}
        loadingMore={loadingMore}
        loadingMoreLabel={loadingMoreLabel}
        onListEndReached={errorLabel ? undefined : onListEndReached}
        hasNextPage={hasNextPage}
        loadedPageCount={loadedPageCount}
        paginationScopeKey={paginationScopeKey}
        onSearchValueChange={onSearchValueChange}
        filterOptions={onSearchValueChange === undefined}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        showOptionsWithoutSearch={showOptionsWithoutSearch}
        triggerAriaLabel={label}
        triggerClassName={compact ? 'h-9 px-3 py-2 text-sm' : 'h-11 px-4 py-3 text-base'}
        itemClassName="text-sm"
      />
      {loading ? null : errorLabel && retryOnOpen ? (
        <Text id={`${id}-error`} role="alert" size="sm" color="destructive">
          {errorLabel}
        </Text>
      ) : errorLabel ? (
        <div role="alert" className="space-y-1">
          <Text size="sm">{errorLabel}</Text>
          {onRetry ? (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              {retryLabel ?? 'Retry'}
            </Button>
          ) : null}
        </div>
      ) : !silentEmpty && options.length === 0 && emptyText ? (
        <Text role="status" size="sm" color="muted">
          {emptyText}
        </Text>
      ) : endLabel ? (
        <Text role="status" size="sm" color="muted">
          {endLabel}
        </Text>
      ) : null}
    </div>
  );
}

export function FilterPopoverInput({
  disabled = false,
  id,
  label,
  max,
  min,
  onChange,
  onCompositionEnd,
  onCompositionStart,
  placeholder,
  preserveInputDraft = false,
  step,
  type = 'text',
  value,
}: FilterPopoverInputProps) {
  if (type === 'date') {
    return (
      <FilterPopoverDateInput
        disabled={disabled}
        id={id}
        label={label}
        max={max}
        min={min}
        onChange={onChange}
        step={step}
        value={value}
      />
    );
  }
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
        value={value ?? ''}
        onCompositionEnd={onCompositionEnd}
        onCompositionStart={onCompositionStart}
        onChange={(event) => {
          const nextValue = preserveInputDraft
            ? event.currentTarget.value
            : event.currentTarget.value.trim();
          onChange(nextValue.length === 0 ? undefined : nextValue);
        }}
      />
    </div>
  );
}

/** Keep native date segments local until the user finishes editing. */
function FilterPopoverDateInput({
  disabled,
  id,
  label,
  max,
  min,
  onChange,
  step,
  value,
}: FilterPopoverInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current !== null) inputRef.current.value = value ?? '';
  }, [value]);

  const commit = () => {
    const input = inputRef.current;
    if (input === null || !input.validity.valid) return;
    const next = input.value;
    if (next !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(next)) return;
    if (next !== (value ?? '')) onChange(next === '' ? undefined : next);
  };

  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        ref={inputRef}
        id={id}
        type="date"
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        defaultValue={value ?? ''}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
    </div>
  );
}

export function FilterPopover({
  activeFilterCount,
  children,
  clearLabel,
  disabled = false,
  label,
  onClear,
  onOpenChange,
  open,
  testId,
  triggerClassName,
}: FilterPopoverProps) {
  const triggerLabel = activeFilterCount > 0 ? `${label} (${String(activeFilterCount)})` : label;

  return (
    <Popover modal={false} open={open} onOpenChange={onOpenChange}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn('relative size-11 shrink-0', triggerClassName)}
                aria-label={triggerLabel}
                disabled={disabled}
              >
                <SlidersHorizontal className="size-4" aria-hidden="true" />
                {activeFilterCount > 0 ? (
                  <CountBadge
                    className="pointer-events-none absolute -right-1.5 -top-1.5"
                    count={activeFilterCount}
                    label={triggerLabel}
                  />
                ) : null}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="flex max-h-[min(32rem,var(--radix-popover-content-available-height))] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-visible rounded-none border border-border bg-background p-0"
        data-testid={testId}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border bg-background px-3 py-2">
          <Text size="sm" className="font-medium text-foreground">
            {label}
          </Text>
          {activeFilterCount > 0 && onClear ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto px-1 py-0 text-xs"
              disabled={disabled}
              onClick={onClear}
            >
              {clearLabel}
            </Button>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-background p-3">{children}</div>
      </PopoverContent>
    </Popover>
  );
}
